import React, { useMemo, useRef, useState } from 'react';
import {
    alpha,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    Grid,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BusinessIcon from '@mui/icons-material/Business';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import { submitPublicUpload } from '../services/publicUploadService';
import { useBackTarget } from '../modules/pricingJourney/navigation';
import PageHeader from '../modules/pricingJourney/components/PageHeader';
import { Eyebrow } from '../modules/pricingJourney/components/JourneyBasics';

type UploadFormState = {
    companyName: string;
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    notes: string;
};

const ACCEPTED_EXTENSIONS = ['csv', 'xlsx', 'xls', 'zip'];
const MAX_FILES = 10;
const MAX_FILE_SIZE_MB = 50;

const DARK_BG = '#0b1120';
const CARD_BG = alpha('#ffffff', 0.025);
const BORDER = '#334155';

const PARAGRAPH_SX = {
    color: '#94a3b8',
    lineHeight: 1.8,
    // PRC-029: sin párrafos justificados.
    textAlign: 'left',
    hyphens: 'none',
    wordBreak: 'normal',
    overflowWrap: 'normal',
};

const formatBytes = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const sanitizeFiles = (incomingFiles: File[], existingFiles: File[]) => {
    const nextErrors: string[] = [];
    const deduped = [...existingFiles];

    for (const file of incomingFiles) {
        const extension = file.name.split('.').pop()?.toLowerCase() || '';

        if (!ACCEPTED_EXTENSIONS.includes(extension)) {
            nextErrors.push(`"${file.name}" no tiene un formato permitido.`);
            continue;
        }

        if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
            nextErrors.push(`"${file.name}" supera el límite de ${MAX_FILE_SIZE_MB} MB.`);
            continue;
        }

        const alreadyExists = deduped.some(
            (f) => f.name === file.name && f.size === file.size && f.lastModified === file.lastModified
        );

        if (alreadyExists) {
            nextErrors.push(`"${file.name}" ya está añadido.`);
            continue;
        }

        if (deduped.length >= MAX_FILES) {
            nextErrors.push(`Solo puedes adjuntar un máximo de ${MAX_FILES} archivos.`);
            break;
        }

        deduped.push(file);
    }

    return { files: deduped, errors: nextErrors };
};

// PRC-027: con `inApp` se usa dentro de la aplicación (/enviar-archivos) y vuelve al origen.
const PublicFileUploadPage: React.FC<{ inApp?: boolean }> = ({ inApp = false }) => {
    const back = useBackTarget('/operations', 'Volver a Operaciones');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const inputRef = useRef<HTMLInputElement | null>(null);

    const [form, setForm] = useState<UploadFormState>({
        companyName: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        notes: '',
    });

    const [files, setFiles] = useState<File[]>([]);
    const [isDragging, setIsDragging] = useState(false);
    const [errors, setErrors] = useState<string[]>([]);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadReference, setUploadReference] = useState<string | null>(null);
    const [uploadedFilesCount, setUploadedFilesCount] = useState<number>(0);

    const totalSize = useMemo(
        () => files.reduce((acc, file) => acc + file.size, 0),
        [files]
    );

    const isFormValid =
        form.companyName.trim() !== '' &&
        form.contactName.trim() !== '' &&
        form.contactEmail.trim() !== '' &&
        /\S+@\S+\.\S+/.test(form.contactEmail) &&
        files.length > 0;

    const handleChange =
        (field: keyof UploadFormState) =>
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setForm((prev) => ({ ...prev, [field]: event.target.value }));
        };

    const handleFilesSelected = (fileList: FileList | null) => {
        if (!fileList) return;
        const result = sanitizeFiles(Array.from(fileList), files);
        setFiles(result.files);
        setErrors(result.errors);
        setSubmitSuccess(false);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(false);
        handleFilesSelected(event.dataTransfer.files);
    };

    const handleDragEvents = (event: React.DragEvent<HTMLDivElement>, active: boolean) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragging(active);
    };

    const removeFile = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setSubmitSuccess(false);
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrors([]);
        setSubmitSuccess(false);
        setUploadReference(null);
        setUploadedFilesCount(0);

        if (!isFormValid) {
            setErrors(['Completa los campos obligatorios y añade al menos un archivo.']);
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await submitPublicUpload({
                companyName: form.companyName,
                contactName: form.contactName,
                contactEmail: form.contactEmail,
                contactPhone: form.contactPhone,
                notes: form.notes,
                files,
            });

            setSubmitSuccess(true);
            setUploadReference(response.public_token);
            setUploadedFilesCount(response.file_count);

            setForm({
                companyName: '',
                contactName: '',
                contactEmail: '',
                contactPhone: '',
                notes: '',
            });
            setFiles([]);

            if (inputRef.current) {
                inputRef.current.value = '';
            }
        } catch (error: any) {
            const detail =
                error?.response?.data?.detail ||
                'No se ha podido completar el envío. Inténtalo de nuevo.';

            setErrors([detail]);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ minHeight: inApp ? 'auto' : '100vh', bgcolor: DARK_BG, color: '#f8fafc' }}>
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
                    background:
                        'radial-gradient(circle at 20% 20%, rgba(56,189,248,0.12), transparent 25%), radial-gradient(circle at 80% 0%, rgba(252,211,77,0.08), transparent 22%), linear-gradient(180deg, #07101d 0%, #0b1120 60%, #0b1120 100%)',
                }}
            >
                <Container maxWidth="lg" sx={{ py: inApp ? 3 : { xs: 8, md: 11 } }}>
                    <Stack spacing={3}>
                        {inApp ? (
                            // PRC-029/032: dentro de la aplicación, la cabecera común.
                            <PageHeader
                                eyebrow="Operaciones"
                                title="Enviar archivos al equipo"
                                description="Comparte tu documentación comercial y nuestro equipo prepara la carga inicial por ti."
                                back={{ label: back.label, onClick: back.go }}
                                sx={{ mb: 0 }}
                            />
                        ) : (
                            <>
                                <Box>
                                    <Button
                                        href="/"
                                        startIcon={<ArrowBackIcon />}
                                        sx={{
                                            color: '#94a3b8',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: 0,
                                            '&:hover': { color: '#f8fafc', bgcolor: 'transparent' },
                                        }}
                                    >
                                        Volver a la web
                                    </Button>
                                </Box>

                                <Stack spacing={2} sx={{ maxWidth: 820 }}>
                                    <Eyebrow tone="sky">Envío de archivos</Eyebrow>
                                    <Typography
                                        variant={isMobile ? 'h4' : 'h2'}
                                        component="h1"
                                        sx={{
                                            fontWeight: 900,
                                            lineHeight: 1.05,
                                            letterSpacing: '-0.03em',
                                        }}
                                    >
                                        Envía tus datos para la auditoría
                                    </Typography>
                                    <Typography
                                        sx={{
                                            ...PARAGRAPH_SX,
                                            maxWidth: 760,
                                            fontSize: { xs: '1rem', md: '1.05rem' },
                                        }}
                                    >
                                        Si prefieres delegar la carga inicial, comparte aquí tu documentación comercial y
                                        nuestro equipo preparará el entorno para la auditoría de rentabilidad. Solo el
                                        equipo técnico responsable del proyecto tiene acceso a lo que envíes.
                                    </Typography>
                                </Stack>
                            </>
                        )}

                        <Grid container spacing={2} sx={{ pt: 2 }}>
                            <Grid item xs={12} md={4}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        height: '100%',
                                        bgcolor: CARD_BG,
                                        border: `1px solid ${alpha('#38bdf8', 0.18)}`,
                                        borderRadius: 3,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        <BusinessIcon sx={{ color: '#38bdf8', mt: 0.25 }} />
                                        <Box>
                                            <Typography sx={{ fontWeight: 800, color: '#f8fafc', mb: 0.5 }}>
                                                Identificación clara
                                            </Typography>
                                            <Typography variant="body2" sx={PARAGRAPH_SX}>
                                                Solicitaremos el nombre de la empresa, contacto y una dirección de correo para poder
                                                clasificar correctamente cada entrega.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        height: '100%',
                                        bgcolor: CARD_BG,
                                        border: `1px solid ${alpha('#38bdf8', 0.18)}`,
                                        borderRadius: 3,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        <DescriptionOutlinedIcon sx={{ color: '#38bdf8', mt: 0.25 }} />
                                        <Box>
                                            <Typography sx={{ fontWeight: 800, color: '#f8fafc', mb: 0.5 }}>
                                                Archivos permitidos
                                            </Typography>
                                            <Typography variant="body2" sx={PARAGRAPH_SX}>
                                                CSV, Excel y ZIP. Es posible adjuntar varios archivos en
                                                una sola entrega.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 3,
                                        height: '100%',
                                        bgcolor: CARD_BG,
                                        border: `1px solid ${alpha('#38bdf8', 0.18)}`,
                                        borderRadius: 3,
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <Stack direction="row" spacing={2} alignItems="flex-start">
                                        <ShieldOutlinedIcon sx={{ color: '#38bdf8', mt: 0.25 }} />
                                        <Box>
                                            <Typography sx={{ fontWeight: 800, color: '#f8fafc', mb: 0.5 }}>
                                                Envío seguro
                                            </Typography>
                                            <Typography variant="body2" sx={PARAGRAPH_SX}>
                                                La zona está preparada para recibir documentación
                                                sin registro, no es necesario crear una cuenta.
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Stack>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
                <Grid container spacing={4} alignItems="stretch">
                    <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 3, md: 4 },
                                bgcolor: CARD_BG,
                                border: `1px solid ${BORDER}`,
                                borderRadius: 4,
                                position: { md: 'sticky' },
                                top: 24,
                                height: '100%',
                                width: '100%',
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                                Datos mínimos recomendados
                            </Typography>

                            <Typography variant="body2" sx={PARAGRAPH_SX}>
                                Para que podamos preparar correctamente la auditoría, recomendamos compartir una
                                estructura mínima de información comercial. Cuanto más completa sea la documentación,
                                mayor será la calidad del análisis inicial.
                            </Typography>

                            <Stack spacing={2.2}>

                                <Divider sx={{ borderColor: alpha('#fff', 0.08) }} />

                                <Box>
                                    <Typography sx={{ color: '#f8fafc', fontWeight: 700, mb: 1 }}>
                                        01. Clientes
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                                        <Chip label="Cliente ID" size="small" sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 800 }} />
                                    </Stack>
                                    <Typography variant="body2" sx={PARAGRAPH_SX}>
                                        Información opcional útil: sector, ubicación, tamaño o cualquier segmentación comercial.
                                    </Typography>
                                </Box>

                                <Divider sx={{ borderColor: alpha('#fff', 0.08) }} />

                                <Box>
                                    <Typography sx={{ color: '#f8fafc', fontWeight: 700, mb: 1 }}>
                                        02. Productos
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                                        <Chip label="Producto ID" size="small" sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 800 }} />
                                        <Chip label="Coste estándar" size="small" sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 800 }} />
                                    </Stack>
                                    <Typography variant="body2" sx={PARAGRAPH_SX}>
                                        Información opcional útil: familia, marca, categoría o estructura de catálogo.
                                    </Typography>
                                </Box>

                                <Divider sx={{ borderColor: alpha('#fff', 0.08) }} />

                                <Box>
                                    <Typography sx={{ color: '#f8fafc', fontWeight: 700, mb: 1 }}>
                                        03. Histórico de ventas
                                    </Typography>
                                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                                        <Chip label="Fecha" size="small" sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 800 }} />
                                        <Chip label="Producto ID" size="small" sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 800 }} />
                                        <Chip label="Cliente ID" size="small" sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 800 }} />
                                        <Chip label="Cantidad" size="small" sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 800 }} />
                                        <Chip label="Precio" size="small" sx={{ bgcolor: '#38bdf8', color: '#0f172a', fontWeight: 800 }} />
                                    </Stack>
                                    <Typography variant="body2" sx={PARAGRAPH_SX}>
                                        Si dispones de descuentos, condiciones comerciales o estructura de tarifa, puedes incluirlos
                                        también: el sistema los descuenta e incorpora al análisis.
                                    </Typography>
                                </Box>
                            </Stack>

                            <Box
                                sx={{
                                    mt: 4,
                                    p: 2.5,
                                    borderRadius: 3,
                                    bgcolor: alpha('#fcd34d', 0.06),
                                    border: `1px solid ${alpha('#fcd34d', 0.2)}`,
                                    minHeight: 170,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography sx={{ color: '#fcd34d', fontWeight: 800, mb: 0.8 }}>
                                    Requisito de entrenamiento
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        ...PARAGRAPH_SX,
                                        color: '#cbd5e1',
                                    }}
                                >
                                    Para garantizar la significancia estadística del modelo, recomendamos compartir un
                                    histórico transaccional de al menos 24 meses. Cuanta más profundidad temporal exista, mayor será
                                    la robustez de la auditoría inicial.
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    mt: 4,
                                    p: 2.5,
                                    borderRadius: 3,
                                    bgcolor: alpha('#22c55e', 0.07),
                                    border: `1px solid ${alpha('#22c55e', 0.22)}`,
                                    minHeight: 170,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography sx={{ color: '#86efac', fontWeight: 800, mb: 0.8 }}>
                                    Privacidad y confidencialidad
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        ...PARAGRAPH_SX,
                                        color: '#d1fae5',
                                    }}
                                >
                                    La documentación se utilizará exclusivamente para preparar la auditoría y entrenar el
                                    entorno de análisis. No compartimos los datos con terceros, el acceso queda restringido
                                    al equipo técnico responsable del proyecto y la información se gestiona en infraestructura
                                    privada y controlada.
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={7} sx={{ display: 'flex' }}>
                        <Paper
                            elevation={0}
                            component="form"
                            onSubmit={handleSubmit}
                            sx={{
                                p: { xs: 3, md: 4 },
                                bgcolor: CARD_BG,
                                border: `1px solid ${BORDER}`,
                                borderRadius: 4,
                                height: '100%',
                                width: '100%',
                            }}
                        >
                            <Stack spacing={3}>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                                        Datos de la entrega
                                    </Typography>
                                    <Typography variant="body2" sx={PARAGRAPH_SX}>
                                        Rellena los campos para que podamos identificar correctamente
                                        su documentación y asociarla a la auditoría.
                                    </Typography>
                                </Box>

                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Empresa"
                                            value={form.companyName}
                                            onChange={handleChange('companyName')}
                                            required
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: alpha('#fff', 0.02),
                                                    color: '#f8fafc',
                                                    borderRadius: 2,
                                                    '& fieldset': { borderColor: BORDER },
                                                    '&:hover fieldset': { borderColor: '#475569' },
                                                    '&.Mui-focused fieldset': { borderColor: '#38bdf8' },
                                                },
                                                '& .MuiInputLabel-root': { color: '#94a3b8' },
                                            }}
                                            InputProps={{
                                                startAdornment: <BusinessIcon sx={{ color: '#64748b', mr: 1 }} />,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Nombre de contacto"
                                            value={form.contactName}
                                            onChange={handleChange('contactName')}
                                            required
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: alpha('#fff', 0.02),
                                                    color: '#f8fafc',
                                                    borderRadius: 2,
                                                    '& fieldset': { borderColor: BORDER },
                                                    '&:hover fieldset': { borderColor: '#475569' },
                                                    '&.Mui-focused fieldset': { borderColor: '#38bdf8' },
                                                },
                                                '& .MuiInputLabel-root': { color: '#94a3b8' },
                                            }}
                                            InputProps={{
                                                startAdornment: <PersonOutlineIcon sx={{ color: '#64748b', mr: 1 }} />,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Email corporativo"
                                            type="email"
                                            value={form.contactEmail}
                                            onChange={handleChange('contactEmail')}
                                            required
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: alpha('#fff', 0.02),
                                                    color: '#f8fafc',
                                                    borderRadius: 2,
                                                    '& fieldset': { borderColor: BORDER },
                                                    '&:hover fieldset': { borderColor: '#475569' },
                                                    '&.Mui-focused fieldset': { borderColor: '#38bdf8' },
                                                },
                                                '& .MuiInputLabel-root': { color: '#94a3b8' },
                                            }}
                                            InputProps={{
                                                startAdornment: <EmailOutlinedIcon sx={{ color: '#64748b', mr: 1 }} />,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Teléfono (opcional)"
                                            value={form.contactPhone}
                                            onChange={handleChange('contactPhone')}
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: alpha('#fff', 0.02),
                                                    color: '#f8fafc',
                                                    borderRadius: 2,
                                                    '& fieldset': { borderColor: BORDER },
                                                    '&:hover fieldset': { borderColor: '#475569' },
                                                    '&.Mui-focused fieldset': { borderColor: '#38bdf8' },
                                                },
                                                '& .MuiInputLabel-root': { color: '#94a3b8' },
                                            }}
                                            InputProps={{
                                                startAdornment: <PhoneOutlinedIcon sx={{ color: '#64748b', mr: 1 }} />,
                                            }}
                                        />
                                    </Grid>

                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            minRows={4}
                                            label="Mensaje (opcional)"
                                            value={form.notes}
                                            onChange={handleChange('notes')}
                                            placeholder="Añade contexto sobre los archivos, periodo analizado o cualquier detalle útil para el equipo."
                                            InputLabelProps={{ shrink: true }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    bgcolor: alpha('#fff', 0.02),
                                                    color: '#f8fafc',
                                                    borderRadius: 2,
                                                    '& fieldset': { borderColor: BORDER },
                                                    '&:hover fieldset': { borderColor: '#475569' },
                                                    '&.Mui-focused fieldset': { borderColor: '#38bdf8' },
                                                },
                                                '& .MuiInputLabel-root': { color: '#94a3b8' },
                                                '& .MuiInputBase-input::placeholder': {
                                                    color: '#64748b',
                                                    opacity: 1,
                                                },
                                            }}
                                        />
                                    </Grid>
                                </Grid>

                                <Divider sx={{ borderColor: alpha('#fff', 0.08) }} />

                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 1.5 }}>
                                        Archivos
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#86efac',
                                            mb: 2,
                                            fontWeight: 600,
                                            lineHeight: 1.7,
                                        }}
                                    >
                                        Documentación privada · Uso exclusivo para auditoría y preparación del entorno
                                    </Typography>

                                    <Box
                                        onDrop={handleDrop}
                                        onDragEnter={(e) => handleDragEvents(e, true)}
                                        onDragOver={(e) => handleDragEvents(e, true)}
                                        onDragLeave={(e) => handleDragEvents(e, false)}
                                        onClick={() => inputRef.current?.click()}
                                        sx={{
                                            border: `2px dashed ${
                                                isDragging ? '#38bdf8' : alpha('#38bdf8', 0.35)
                                            }`,
                                            bgcolor: isDragging
                                                ? alpha('#38bdf8', 0.08)
                                                : alpha('#38bdf8', 0.04),
                                            borderRadius: 3,
                                            p: { xs: 3, md: 4 },
                                            minHeight: { xs: 220, md: 280 },
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                bgcolor: alpha('#38bdf8', 0.06),
                                                borderColor: '#38bdf8',
                                            },
                                        }}
                                    >
                                        <input
                                            ref={inputRef}
                                            hidden
                                            multiple
                                            type="file"
                                            accept=".csv,.xlsx,.xls,.zip"
                                            onChange={(e) => handleFilesSelected(e.target.files)}
                                        />

                                        <CloudUploadIcon sx={{ fontSize: 42, color: '#38bdf8', mb: 1 }} />
                                        <Typography sx={{ color: '#f8fafc', fontWeight: 800, mb: 0.5 }}>
                                            Arrastra aquí tus archivos o haz clic para seleccionarlos
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#94a3b8', mb: 1 }}>
                                            Formatos permitidos: CSV, XLSX, XLS y ZIP
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                                            Máximo {MAX_FILES} archivos · {MAX_FILE_SIZE_MB} MB por archivo
                                        </Typography>
                                    </Box>
                                </Box>

                                {files.length > 0 && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            bgcolor: alpha('#fff', 0.02),
                                            border: `1px solid ${alpha('#fff', 0.08)}`,
                                            borderRadius: 3,
                                        }}
                                    >
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            justifyContent="space-between"
                                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                                            spacing={1}
                                            sx={{ mb: 2 }}
                                        >
                                            <Typography sx={{ fontWeight: 800, color: '#f8fafc' }}>
                                                Archivos adjuntos ({files.length})
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                                Tamaño total: {formatBytes(totalSize)}
                                            </Typography>
                                        </Stack>

                                        <Stack spacing={1.2}>
                                            {files.map((file, index) => (
                                                <Box
                                                    key={`${file.name}-${file.lastModified}-${index}`}
                                                    sx={{
                                                        p: 1.5,
                                                        borderRadius: 2,
                                                        border: `1px solid ${alpha('#fff', 0.08)}`,
                                                        bgcolor: alpha('#fff', 0.02),
                                                    }}
                                                >
                                                    <Stack
                                                        direction="row"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        spacing={2}
                                                    >
                                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                                            <InsertDriveFileOutlinedIcon sx={{ color: '#38bdf8' }} />
                                                            <Box>
                                                                <Typography sx={{ color: '#f8fafc', fontWeight: 700 }}>
                                                                    {file.name}
                                                                </Typography>
                                                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                                                    {formatBytes(file.size)}
                                                                </Typography>
                                                            </Box>
                                                        </Stack>

                                                        <IconButton
                                                            onClick={() => removeFile(index)}
                                                            sx={{
                                                                color: '#94a3b8',
                                                                '&:hover': {
                                                                    color: '#f87171',
                                                                    bgcolor: alpha('#f87171', 0.08),
                                                                },
                                                            }}
                                                        >
                                                            <DeleteOutlineIcon />
                                                        </IconButton>
                                                    </Stack>
                                                </Box>
                                            ))}
                                        </Stack>
                                    </Paper>
                                )}

                                {errors.length > 0 && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 3,
                                            bgcolor: alpha('#ef4444', 0.08),
                                            border: `1px solid ${alpha('#ef4444', 0.2)}`,
                                        }}
                                    >
                                        <Stack spacing={0.7}>
                                            {errors.map((error, index) => (
                                                <Typography key={index} variant="body2" sx={{ color: '#fecaca' }}>
                                                    • {error}
                                                </Typography>
                                            ))}
                                        </Stack>
                                    </Paper>
                                )}

                                {submitSuccess && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2,
                                            borderRadius: 3,
                                            bgcolor: alpha('#22c55e', 0.08),
                                            border: `1px solid ${alpha('#22c55e', 0.22)}`,
                                        }}
                                    >
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <CheckCircleOutlineIcon sx={{ color: '#4ade80' }} />
                                            <Box>
                                                <Typography sx={{ color: '#dcfce7', fontWeight: 800 }}>
                                                    Documentación recibida correctamente
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#bbf7d0', lineHeight: 1.7 }}>
                                                    Hemos recibido sus archivos y nuestro equipo revisará la documentación para preparar
                                                    la auditoría en un entorno privado y controlado. Referencia de entrega:{' '}
                                                    <Box component="span" sx={{ fontWeight: 800 }}>
                                                        {uploadReference}
                                                    </Box>
                                                    {uploadedFilesCount > 0 && (
                                                        <>
                                                            {' '}· Archivos recibidos: {uploadedFilesCount}
                                                        </>
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                )}

                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                    spacing={2}
                                >
                                    <Typography variant="body2" sx={PARAGRAPH_SX}>
                                        Tras el envío, nuestro equipo revisará la documentación y preparará la carga inicial
                                        para la auditoría en un entorno privado, restringido y controlado.
                                    </Typography>

                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={!isFormValid || isSubmitting}
                                        startIcon={<CloudUploadIcon />}
                                        sx={{
                                            bgcolor: '#fcd34d',
                                            color: '#0f172a',
                                            px: 4,
                                            py: 1.4,
                                            fontWeight: 900,
                                            '&:hover': { bgcolor: '#fbbf24' },
                                            '&.Mui-disabled': {
                                                bgcolor: alpha('#fcd34d', 0.35),
                                                color: alpha('#0f172a', 0.7),
                                            },
                                        }}
                                    >
                                        {isSubmitting ? 'Preparando envío...' : 'Enviar archivos'}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Paper>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default PublicFileUploadPage;